
import { Vue, Options, prop } from "vue-class-component";
import MainLoading from "@/app/ui/views/shipment/booking/component/product-poster/loading/main-loading.vue";
import ToggleV2 from "@/app/ui/components/toggle-v2/index.vue";
import OverlayPanel from "primevue/overlaypanel";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import {
  PosterProduct,
  ProductTypePoster
} from "@/domain/entities/CreateBookingV3";
import {
  formatByCountry,
  formatInputMoney,
  formatInputMoneyWithDecimal,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import { productStyle } from "./data";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";
import formatPriceWithoutCurrency from "@/app/infrastructures/misc/common-library/FormatPriceWithoutCurrency";
import formatPrice from "@/app/infrastructures/misc/common-library/v2/FormatPrice";
import formatPriceRM from "@/app/infrastructures/misc/common-library/v2/FormatPriceRM";
import { applyRecommendationOrder } from "@/app/ui/views/shipment/booking/create-booking-v3/utils/applyRecommendationOrder";

class Props {
  id = prop<string>({
    default: "",
    type: String
  });
  modelValue = prop<string>({
    default: "",
    type: String
  });
  listProduct = prop<Array<string>>({
    default: [],
    type: Array
  });
  upsellingData = prop<Array<PosterProduct>>({
    default: [],
    type: Array
  });
  isLoadingUpselling = prop<boolean>({
    default: false,
    type: Boolean
  });
  isErrorUpselling = prop<boolean>({
    default: false,
    type: Boolean
  });
  isBookingRetail = prop<boolean>({
    default: true,
    type: Boolean
  });
  productIndex = prop<number>({
    default: 0,
    type: Number
  });
  currentIndex = prop<number>({
    default: 0,
    type: Number
  });
  isEmbargo = prop<boolean>({
    default: false,
    type: Boolean
  });
  configProductType = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configTariff = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  formType = prop<string>({
    default: "manual",
    type: String
  });
  fullHeight = prop<boolean>({
    default: false,
    type: Boolean
  });
  isEditSttMY = prop<boolean>({
    default: false,
    type: Boolean
  });
  isEditStt = prop<boolean>({
    default: false,
    type: Boolean
  });
  isCustomerApps = prop<boolean>({
    default: false
  });
  isErrorEstimation = prop<string>({
    default: "",
    type: String
  });
  clientData = prop<any>({
    default: {},
    type: Object
  });
}

@Options({
  emits: ["update:modelValue", "retry", "setProduct"],
  components: {
    MainLoading,
    ToggleV2,
    Skeleton,
    OverlayPanel
  }
})
export default class Product extends Vue.with(Props) {
  isDiscountAgentHide = true;

  discountAgentHide(status: boolean, embargo: boolean) {
    return (
      !this.isDiscountAgentHide ||
      this.isEmbargo ||
      !status ||
      embargo ||
      !this.isBookingRetail ||
      this.getRuleRuleTariffDisabled
    );
  }

  formatDiscountAgent(discount: number) {
    return formatByCountry(discount, {
      type: "percentage",
      country: this.accountCountryCode
    });
  }

  get getRuleProductTypeDisabled() {
    return this.configProductType.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get getRuleRuleTariffDisabled() {
    return this.configTariff.rule.includes(ConfigValidationForm.NON_SHOW);
  }

  get prefixCurrency() {
    if (this.clientData?.id)
      return this.clientData?.clientCountry.toLowerCase() === "id"
        ? "Rp"
        : "RM";
    return !this.accountIsNonForeign || this.isEditSttMY ? "RM" : "Rp";
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  get activeProducts() {
    return this.modelValue;
  }

  set activeProducts(productType: string) {
    this.$emit("update:modelValue", productType);
  }

  get isFormBookClient() {
    return this.formType === "client";
  }

  get bookForClientCurrency() {
    const clientCountry = this.clientData?.clientCountry;
    return clientCountry.toLowerCase() === "id" ? "Rp••••••" : "RM••••••";
  }

  get maskingValue() {
    if (this.clientData?.id) return this.bookForClientCurrency;
    return this.isEditSttMY ? "RM••••••" : "Rp••••••";
  }

  get isAccountClient() {
    return AccountController.accountData.isAllClient;
  }

  toggleReturnConfig() {
    this.isDiscountAgentHide = !this.isDiscountAgentHide;
  }

  onOpenTooltip(event: any) {
    const refs: any = this.$refs;
    refs.openTooltip.toggle(event);
  }

  getProductType() {
    this.activeProducts = "";
    this.$emit("retry");
  }

  setProduct(product = "") {
    this.activeProducts = product;
    this.$emit("setProduct");
  }

  formatEstimationPrice(curr: string) {
    return formatByCountry(this.parseNumber(curr), {
      type: "money",
      country: this.accountCountryCode,
      prefix: this.accountCountryCode === "MY" ? "RM" : "Rp",
      fractionDigits: 2
    });
  }

  parseNumber(val: string) {
    return parseByCountry(val, {
      country: this.accountCountryCode
    });
  }

  get accountCountryCode() {
    return AccountController.accountData.countryCode?.toUpperCase();
  }

  productTypeAsset(value = "") {
    return productStyle[value as any];
  }

  cardActiveValidation(status = false, embargo = false, selected = false) {
    return (
      this.isEmbargo ||
      !status ||
      embargo ||
      (!selected && this.getRuleProductTypeDisabled)
    );
  }

  discountAgentValidation(status = false, embargo = false) {
    return this.isEmbargo || !status || embargo || !this.isBookingRetail;
  }

  get getListProduct(): ProductTypePoster[] {
    const product = this.isBookingRetail
      ? applyRecommendationOrder(this.listProduct, this.upsellingData)
      : this.listProduct;

    // if (!this.accountIsNonForeign) {
    //   product = product.filter(
    //     (item) => item === "INTERPACK" || item === "INTERPACKMAXI" || item === "REGPACK"
    //   );
    // } else {
    // const clientCountryCode = this.clientData?.id
    //   ? this.clientData?.clientCountry.toLowerCase()
    //   : "id";
    // const isInternalAndBookForMy =
    //   this.isInternalAccount && clientCountryCode === "my";
    // if (!isInternalAndBookForMy) {
    //   product = product.filter((item) => item !== "INTERPACKMAXI");
    // }
    // }

    return product.map((type) => {
      const upsellingItem = this.findUpsellingItem(type);
      return this.buildProductPoster(type, upsellingItem);
    });
  }

  get isNoRecommendation() {
    return !this.getListProduct.filter(p => p.isRecommended).length;
  }

  private findUpsellingItem(type = ""): PosterProduct | undefined {
    return this.upsellingData.find(
      (item) => item.productType.toLowerCase() === type.toLowerCase()
    );
  }

  private buildProductPoster(type = "", item?: any): ProductTypePoster {
    return new ProductTypePoster({
      name: type,
      estimateSla: item?.estimateSla.replace(" Hari", "") ?? "",
      totalTariffAfterDiscount: item?.totalTariffAfterDiscount ?? 0,
      commissionRate: item?.commissionRate ?? 0,
      bookingCommission: item?.bookingCommission ?? 0,
      isEmbargo: item?.isEmbargo ?? false,
      statusActive: !!item,
      shippingCost: item?.shippingCost,
      isMasking: item?.isMasking ?? false,
      isRecommended: item?.isRecommended ?? false,
    });
  }

  get isInternalAccount() {
    return AccountController.accountData.isInternalAccount;
  }
}
